import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBlogDetail, fetchBlogsPage } from '../store/blogsSlice';
import { getApiBaseUrl, resolveMediaUrl } from '../utils/media';

const baseStyles = {
  shell: {
    maxWidth: 1320,
    margin: '0 auto',
    padding: '32px 20px 56px',
    fontFamily: '"Segoe UI", Arial, sans-serif',
    color: '#0f172a',
    background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 20%, #f8fafc 100%)',
  },
  header: {
    textAlign: 'center',
    marginBottom: 48,
    padding: '0 16px',
  },
  title: {
    margin: 0,
    fontSize: 44,
    fontWeight: 800,
    lineHeight: 1.1,
    letterSpacing: '-0.03em',
  },
  subtitle: {
    marginTop: 12,
    color: '#64748b',
    fontSize: 18,
    lineHeight: 1.75,
    maxWidth: 760,
    margin: '16px auto 0',
  },
  grid: {
    display: 'grid',
    gap: 28,
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
  },
  card: {
    background: '#ffffff',
    borderRadius: 24,
    overflow: 'hidden',
    border: '1px solid rgba(148, 163, 184, 0.18)',
    boxShadow: '0 16px 40px rgba(15, 23, 42, 0.08)',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  cardImage: {
    width: '100%',
    height: 200,
    objectFit: 'cover',
  },
  cardImagePlaceholder: {
    width: '100%',
    height: 200,
    background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#94a3b8',
    fontSize: 48,
  },
  cardBody: {
    padding: 24,
  },
  cardMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    fontSize: 13,
    color: '#64748b',
    marginBottom: 12,
  },
  cardTitle: {
    margin: '0 0 8px',
    fontSize: 22,
    fontWeight: 700,
    lineHeight: 1.35,
    color: '#0f172a',
  },
  cardExcerpt: {
    margin: 0,
    fontSize: 15,
    color: '#475569',
    lineHeight: 1.75,
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  featuredBadge: {
    display: 'inline-block',
    padding: '6px 12px',
    background: '#dbeafe',
    color: '#1d4ed8',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    marginBottom: 12,
  },
  backButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 18px',
    background: '#f1f5f9',
    border: 'none',
    borderRadius: 10,
    color: '#475569',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    marginBottom: 24,
  },
  detailLayout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.7fr) minmax(280px, 0.8fr)',
    gap: 32,
    alignItems: 'start',
  },
  articlePanel: {
    background: '#ffffff',
    border: '1px solid rgba(148, 163, 184, 0.18)',
    borderRadius: 28,
    padding: 32,
    boxShadow: '0 20px 48px rgba(15, 23, 42, 0.08)',
  },
  blogHeader: {
    marginBottom: 28,
  },
  blogMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    fontSize: 14,
    color: '#64748b',
    marginBottom: 16,
  },
  blogTitle: {
    margin: '0 0 16px',
    fontSize: 46,
    fontWeight: 800,
    lineHeight: 1.1,
    letterSpacing: '-0.03em',
  },
  blogImage: {
    width: '100%',
    height: 'auto',
    maxHeight: 520,
    objectFit: 'cover',
    borderRadius: 22,
    margin: '0 auto 32px',
    display: 'block',
  },
  blogContent: {
    fontSize: 17,
    lineHeight: 1.9,
    color: '#334155',
  },
  sidebar: {
    position: 'sticky',
    top: 24,
  },
  sidebarCard: {
    background: '#ffffff',
    border: '1px solid rgba(148, 163, 184, 0.18)',
    borderRadius: 24,
    padding: 24,
    boxShadow: '0 16px 40px rgba(15, 23, 42, 0.06)',
  },
  sidebarTitle: {
    margin: '0 0 18px',
    fontSize: 18,
    fontWeight: 800,
    color: '#0f172a',
  },
  sidebarList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  sidebarItem: {
    display: 'flex',
    gap: 14,
    alignItems: 'flex-start',
    padding: 14,
    borderRadius: 18,
    border: '1px solid rgba(226, 232, 240, 0.9)',
    background: '#f8fafc',
    cursor: 'pointer',
  },
  sidebarThumb: {
    width: 84,
    height: 84,
    borderRadius: 14,
    objectFit: 'cover',
    flexShrink: 0,
    background: '#e2e8f0',
  },
  sidebarItemTitle: {
    margin: '0 0 6px',
    fontSize: 15,
    lineHeight: 1.45,
    fontWeight: 700,
    color: '#0f172a',
  },
  sidebarItemMeta: {
    margin: 0,
    fontSize: 12,
    lineHeight: 1.6,
    color: '#64748b',
  },
  loading: {
    textAlign: 'center',
    padding: 60,
    color: '#64748b',
    fontSize: 16,
  },
  error: {
    textAlign: 'center',
    padding: 40,
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: 12,
    color: '#b91c1c',
    marginBottom: 24,
  },
  empty: {
    textAlign: 'center',
    padding: 60,
    color: '#64748b',
    fontSize: 16,
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    gap: 8,
    marginTop: 40,
  },
  pageButton: {
    padding: '10px 16px',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    background: '#fff',
    color: '#475569',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  pageButtonActive: {
    background: '#2563eb',
    color: '#fff',
    border: '1px solid #2563eb',
  },
  stats: {
    display: 'flex',
    justifyContent: 'center',
    gap: 32,
    marginTop: 16,
    fontSize: 14,
    color: '#64748b',
  },
};

function injectStyles() {
  if (typeof document === 'undefined') return;

  if (document.getElementById('blogs-responsive-styles')) return;

  const style = document.createElement('style');
  style.id = 'blogs-responsive-styles';
  style.textContent = `
    .blogs-shell * { box-sizing: border-box; }
    @media (max-width: 640px) {
      .blogs-shell { padding: 14px !important; }
      .blogs-title { font-size: 32px !important; }
      .blogs-grid { grid-template-columns: 1fr !important; }
      .blog-title { font-size: 32px !important; }
      .blog-content { font-size: 15px !important; }
      .blog-article-panel { padding: 20px !important; border-radius: 22px !important; }
      .blog-detail-layout { grid-template-columns: 1fr !important; gap: 20px !important; }
      .blog-sidebar { position: static !important; }
    }
    @media (max-width: 960px) {
      .blog-detail-layout { grid-template-columns: 1fr !important; }
      .blog-sidebar { position: static !important; }
      .blog-title { font-size: 38px !important; }
    }
    .blog-card:hover { transform: translateY(-4px); box-shadow: 0 12px 40px rgba(15, 23, 42, 0.12); }
    .blog-content { word-break: break-word; }
    .blog-content > :first-child { margin-top: 0 !important; }
    .blog-content > :last-child { margin-bottom: 0 !important; }
    .blog-content p { margin: 0 0 1.35em; font-size: 1em; line-height: 1.9; color: #334155; }
    .blog-content img { display: block; max-width: 100% !important; width: auto; height: auto !important; border-radius: 18px; margin: 1.75em auto; box-shadow: 0 16px 32px rgba(15, 23, 42, 0.08); }
    .blog-content figure { margin: 2em 0; }
    .blog-content figure img { margin-bottom: 0.75em; }
    .blog-content figcaption { font-size: 0.9em; line-height: 1.6; color: #64748b; text-align: center; }
    .blog-content h1, .blog-content h2, .blog-content h3, .blog-content h4 { margin: 1.75em 0 0.65em; font-weight: 800; line-height: 1.25; color: #0f172a; }
    .blog-content h1 { font-size: 2em; }
    .blog-content h2 { font-size: 1.6em; }
    .blog-content h3 { font-size: 1.28em; }
    .blog-content h4 { font-size: 1.1em; }
    .blog-content ul, .blog-content ol { margin: 1.1em 0 1.5em; padding-left: 1.5em; }
    .blog-content li { margin: 0.5em 0; }
    .blog-content a { color: #2563eb; text-decoration: underline; text-underline-offset: 0.18em; }
    .blog-content blockquote { border-left: 4px solid #2563eb; background: #eff6ff; padding: 1em 1.25em; border-radius: 0 16px 16px 0; margin: 1.75em 0; color: #475569; font-style: italic; }
    .blog-content table { width: 100%; border-collapse: collapse; margin: 1.75em 0; overflow: hidden; border-radius: 14px; }
    .blog-content th, .blog-content td { border: 1px solid #e2e8f0; padding: 0.85em 1em; text-align: left; }
    .blog-content th { background: #f8fafc; color: #0f172a; font-weight: 700; }
    .blog-content iframe, .blog-content video { width: 100%; max-width: 100%; border: 0; border-radius: 18px; margin: 1.75em 0; }
    .blog-content strong { color: #0f172a; }
    .back-btn:hover { background: #e2e8f0; }
    .sidebar-blog-item:hover { background: #eff6ff; border-color: rgba(59, 130, 246, 0.2); }
  `;
  document.head.appendChild(style);
}

function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function absolutizeUrl(apiBaseUrl, value) {
  return resolveMediaUrl(value, apiBaseUrl);
}

function normalizeBlogHtml(html, apiBaseUrl) {
  if (!html) return '';
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') return html;

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
    const root = doc.body.firstElementChild;

    if (!root) return html;

    root.querySelectorAll('img').forEach((img) => {
      const src = img.getAttribute('src');
      if (src) {
        img.setAttribute('src', absolutizeUrl(apiBaseUrl, src));
      }
      img.removeAttribute('width');
      img.removeAttribute('height');
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
    });

    root.querySelectorAll('a').forEach((anchor) => {
      const href = anchor.getAttribute('href');
      if (href && !href.startsWith('#')) {
        anchor.setAttribute('href', absolutizeUrl(apiBaseUrl, href));
      }
    });

    return root.innerHTML;
  } catch {
    return html;
  }
}

function LoadingSpinner() {
  return (
    <div style={baseStyles.loading}>
      <div>Loading...</div>
    </div>
  );
}

function BlogCard({ blog, onClick, apiBaseUrl }) {
  const [hover, setHover] = useState(false);
  const featuredImageUrl = absolutizeUrl(apiBaseUrl, blog.featured_image_url);

  return (
    <div
      className="blog-card"
      style={{
        ...baseStyles.card,
        transform: hover ? 'translateY(-4px)' : 'none',
        boxShadow: hover ? '0 12px 40px rgba(15, 23, 42, 0.12)' : '0 4px 20px rgba(15, 23, 42, 0.08)',
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => onClick(blog)}
    >
      {featuredImageUrl ? (
        <img
          style={baseStyles.cardImage}
          src={featuredImageUrl}
          alt={blog.title}
          loading="lazy"
        />
      ) : (
        <div style={baseStyles.cardImagePlaceholder}>
          <i className="fas fa-newspaper" />
        </div>
      )}
      <div style={baseStyles.cardBody}>
        {blog.is_featured && <span style={baseStyles.featuredBadge}>Featured</span>}
        <div style={baseStyles.cardMeta}>
          <span>{formatDate(blog.published_at)}</span>
          {blog.author?.name && <span>By {blog.author.name}</span>}
          {blog.view_count > 0 && <span>{blog.view_count} views</span>}
        </div>
        <h3 style={baseStyles.cardTitle}>{blog.title}</h3>
        {blog.excerpt && <p style={baseStyles.cardExcerpt}>{blog.excerpt}</p>}
      </div>
    </div>
  );
}

function BlogListView({ blogs, onBlogClick, loading }) {
  return (
    <div>
      <div style={baseStyles.grid} className="blogs-grid">
        {blogs.map((blog) => (
          <BlogCard key={blog.id} blog={blog} onClick={onBlogClick} apiBaseUrl={getApiBaseUrl()} />
        ))}
      </div>
      {blogs.length === 0 && !loading && (
        <div style={baseStyles.empty}>
          <div>No blog posts available at the moment.</div>
        </div>
      )}
    </div>
  );
}

function BlogSidebar({ blogs, activeSlug, onSelect }) {
  const otherBlogs = blogs.filter((item) => item.slug !== activeSlug).slice(0, 6);

  if (otherBlogs.length === 0) {
    return null;
  }

  return (
    <aside style={baseStyles.sidebar} className="blog-sidebar">
      <div style={baseStyles.sidebarCard}>
        <h2 style={baseStyles.sidebarTitle}>Other Blogs</h2>
        <div style={baseStyles.sidebarList}>
          {otherBlogs.map((item) => {
            const sidebarImageUrl = absolutizeUrl(getApiBaseUrl(), item.featured_image_url);

            return (
            <div
              key={item.id}
              className="sidebar-blog-item"
              style={baseStyles.sidebarItem}
              onClick={() => onSelect(item)}
            >
              {sidebarImageUrl ? (
                <img
                  src={sidebarImageUrl}
                  alt={item.title}
                  style={baseStyles.sidebarThumb}
                  loading="lazy"
                />
              ) : (
                <div style={baseStyles.sidebarThumb} />
              )}
              <div>
                <h3 style={baseStyles.sidebarItemTitle}>{item.title}</h3>
                <p style={baseStyles.sidebarItemMeta}>{formatDate(item.published_at)}</p>
                {item.excerpt && <p style={baseStyles.sidebarItemMeta}>{item.excerpt}</p>}
              </div>
            </div>
            );
          })}
        </div>
      </div>
    </aside>
  );
}

function BlogDetailView({ blog, blogs, onBack, onSelectBlog, apiBaseUrl }) {
  const normalizedContent = normalizeBlogHtml(blog.content, apiBaseUrl);
  const featuredImageUrl = absolutizeUrl(apiBaseUrl, blog.featured_image_url);

  return (
    <div>
      <button
        className="back-btn"
        style={baseStyles.backButton}
        onClick={onBack}
      >
        <i className="fas fa-arrow-left" />
        Back to Blogs
      </button>

      <div style={baseStyles.detailLayout} className="blog-detail-layout">
        <article style={baseStyles.articlePanel} className="blog-article-panel">
          <header style={baseStyles.blogHeader}>
            {blog.is_featured && <span style={baseStyles.featuredBadge}>Featured</span>}
            <h1 className="blog-title" style={baseStyles.blogTitle}>{blog.title}</h1>
            <div style={baseStyles.blogMeta}>
              {blog.published_at && <span>{formatDate(blog.published_at)}</span>}
              {blog.author?.name && <span>By {blog.author.name}</span>}
              {blog.view_count > 0 && <span>{blog.view_count} views</span>}
            </div>
          </header>

          {featuredImageUrl && (
            <img
              style={baseStyles.blogImage}
              src={featuredImageUrl}
              alt={blog.title}
            />
          )}

          <div
            className="blog-content"
            style={baseStyles.blogContent}
            dangerouslySetInnerHTML={{ __html: normalizedContent }}
          />
        </article>

        <BlogSidebar blogs={blogs} activeSlug={blog.slug} onSelect={onSelectBlog} />
      </div>
    </div>
  );
}

export function BlogsPage({
  apiBaseUrl = getApiBaseUrl(),
  pageSize = 9,
  onBlogClick,
}) {
  const dispatch = useDispatch();
  const [view, setView] = useState('list');
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const { items: blogs, status, error, detailStatusBySlug } = useSelector((state) => state.blogs);
  const loading = status === 'loading' && blogs.length === 0;
  const loadingBlog = selectedBlog ? detailStatusBySlug[selectedBlog.slug] === 'loading' : false;
  const totalPages = Math.ceil((blogs.length || 0) / pageSize) || 1;

  useEffect(() => {
    injectStyles();
  }, []);

  useEffect(() => {
    dispatch(fetchBlogsPage({ apiBaseUrl }));
  }, [apiBaseUrl, dispatch]);

  const handleBlogClick = useCallback(async (blog) => {
    setSelectedBlog(blog);
    setView('detail');

    if (onBlogClick) {
      onBlogClick(blog);
    }

    if (!blog.content) {
      dispatch(fetchBlogDetail({ apiBaseUrl, slug: blog.slug }))
        .unwrap()
        .then(({ blog: fullBlog }) => {
          setSelectedBlog(fullBlog);
        })
        .catch(() => {});
    }
  }, [apiBaseUrl, dispatch, onBlogClick]);

  const handleBack = useCallback(() => {
    setView('list');
    setSelectedBlog(null);
  }, []);

  useEffect(() => {
    if (!selectedBlog?.slug) return;

    const updatedBlog = blogs.find((item) => item.slug === selectedBlog.slug);
    if (updatedBlog) {
      setSelectedBlog(updatedBlog);
    }
  }, [blogs, selectedBlog]);

  const paginatedBlogs = React.useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return blogs.slice(start, start + pageSize);
  }, [blogs, currentPage, pageSize]);

/*   const title = pageContent?.title && pageContent.title.length > 2 ? pageContent.title : 'Health Blog';
  const subtitle = pageContent?.subtitle && pageContent.subtitle.length > 2 ? pageContent.subtitle : 'Latest updates and health insights from our team.'; */

  const title = 'Health Blog';
  const subtitle = 'Latest updates and health insights from our team.';
  return (
    <div style={baseStyles.shell} className="blogs-shell">
      {error && <div style={baseStyles.error}>{error}</div>}

      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {view === 'list' && (
            <>
              <header style={baseStyles.header}>
                <h1 className="blogs-title" style={baseStyles.title}>{title}</h1>
                {subtitle && <p style={baseStyles.subtitle}>{subtitle}</p>}
                {blogs.length > 0 && (
                  <div style={baseStyles.stats}>
                    <span>{blogs.length} article{blogs.length !== 1 ? 's' : ''}</span>
                  </div>
                )}
              </header>

              <BlogListView
                blogs={paginatedBlogs}
                onBlogClick={handleBlogClick}
                loading={loading}
              />

              {totalPages > 1 && (
                <div style={baseStyles.pagination}>
                  <button
                    style={{
                      ...baseStyles.pageButton,
                      opacity: currentPage === 1 ? 0.5 : 1,
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    }}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <i className="fas fa-chevron-left" />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      style={{
                        ...baseStyles.pageButton,
                        ...(currentPage === page ? baseStyles.pageButtonActive : {}),
                      }}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    style={{
                      ...baseStyles.pageButton,
                      opacity: currentPage === totalPages ? 0.5 : 1,
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    }}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <i className="fas fa-chevron-right" />
                  </button>
                </div>
              )}
            </>
          )}

          {view === 'detail' && selectedBlog && (
            <>
              {loadingBlog && <LoadingSpinner />}
              <BlogDetailView
                blog={selectedBlog}
                blogs={blogs}
                onBack={handleBack}
                onSelectBlog={handleBlogClick}
                apiBaseUrl={apiBaseUrl}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}

export function BlogList({ apiBaseUrl = getApiBaseUrl(), pageSize = 9 }) {
  return (
    <BlogsPage apiBaseUrl={apiBaseUrl} pageSize={pageSize} />
  );
}

export function BlogDetail({ apiBaseUrl = getApiBaseUrl(), slug }) {
  const dispatch = useDispatch();
  const { items: blogs, status, error, detailStatusBySlug, detailErrorsBySlug } = useSelector((state) => state.blogs);
  const blog = blogs.find((item) => item.slug === slug) || null;
  const loading = status === 'loading' || detailStatusBySlug[slug] === 'loading';
  const detailError = detailErrorsBySlug[slug] || '';

  useEffect(() => {
    injectStyles();
  }, []);

  useEffect(() => {
    if (!slug) {
      return;
    }

    dispatch(fetchBlogsPage({ apiBaseUrl }));
    dispatch(fetchBlogDetail({ apiBaseUrl, slug }));
  }, [apiBaseUrl, dispatch, slug]);

  return (
    <div style={baseStyles.shell} className="blogs-shell">
      {loading && <LoadingSpinner />}
      {(detailError || error) && <div style={baseStyles.error}>{detailError || error}</div>}
      {!loading && !error && blog && (
        <BlogDetailView
          blog={blog}
          blogs={blogs}
          onBack={() => window.history.back()}
          onSelectBlog={(nextBlog) => {
            if (nextBlog?.slug && typeof window !== 'undefined') {
              window.location.href = `/blog/${nextBlog.slug}`;
            }
          }}
          apiBaseUrl={apiBaseUrl}
        />
      )}
    </div>
  );
}

export default BlogsPage;
